import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import {
  createCadenceTheme,
  defaultPreferences,
  type ThemePreferences,
} from './theme';

// ---------------------------------------------------------------------------
// LocalStorage keys
// ---------------------------------------------------------------------------

const LS_KEYS = {
  mode: 'cadence_theme',
  highContrast: 'cadence_high_contrast',
  fontFamily: 'cadence_font_family',
  fontSize: 'cadence_font_size',
  reducedMotion: 'cadence_reduced_motion',
} as const;

// ---------------------------------------------------------------------------
// Helpers: read / write localStorage
// ---------------------------------------------------------------------------

function readPreferences(): ThemePreferences {
  try {
    return {
      mode:
        (localStorage.getItem(LS_KEYS.mode) as ThemePreferences['mode']) ??
        defaultPreferences.mode,
      highContrast: localStorage.getItem(LS_KEYS.highContrast) === 'true',
      fontFamily:
        (localStorage.getItem(LS_KEYS.fontFamily) as ThemePreferences['fontFamily']) ??
        defaultPreferences.fontFamily,
      fontSize:
        (localStorage.getItem(LS_KEYS.fontSize) as ThemePreferences['fontSize']) ??
        defaultPreferences.fontSize,
      reducedMotion: localStorage.getItem(LS_KEYS.reducedMotion) === 'true',
    };
  } catch {
    // SSR or storage unavailable
    return defaultPreferences;
  }
}

function writePreferences(prefs: ThemePreferences): void {
  try {
    localStorage.setItem(LS_KEYS.mode, prefs.mode);
    localStorage.setItem(LS_KEYS.highContrast, String(prefs.highContrast));
    localStorage.setItem(LS_KEYS.fontFamily, prefs.fontFamily);
    localStorage.setItem(LS_KEYS.fontSize, prefs.fontSize);
    localStorage.setItem(LS_KEYS.reducedMotion, String(prefs.reducedMotion));
  } catch {
    // Silently ignore write failures (e.g. quota exceeded)
  }
}

// ---------------------------------------------------------------------------
// OpenDyslexic font loader
// ---------------------------------------------------------------------------

const DYSLEXIC_FONT_HREF = 'https://fonts.cdnfonts.com/css/opendyslexic';
const DYSLEXIC_LINK_ID = 'cadence-dyslexic-font';

function ensureDyslexicFont(enabled: boolean): void {
  const existing = document.getElementById(DYSLEXIC_LINK_ID);
  if (enabled && !existing) {
    const link = document.createElement('link');
    link.id = DYSLEXIC_LINK_ID;
    link.rel = 'stylesheet';
    link.href = DYSLEXIC_FONT_HREF;
    document.head.appendChild(link);
  } else if (!enabled && existing) {
    existing.remove();
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ThemePreferencesContextValue {
  preferences: ThemePreferences;
  setPreferences: (prefs: ThemePreferences) => void;
  resolvedMode: 'dark' | 'light';
}

export const ThemePreferencesContext = createContext<
  ThemePreferencesContextValue | undefined
>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function CadenceThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [preferences, setPreferencesState] = useState<ThemePreferences>(readPreferences);

  // Track the OS color scheme for 'system' mode
  const [systemDark, setSystemDark] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for OS theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Resolve the effective mode
  const resolvedMode: 'dark' | 'light' = useMemo(() => {
    if (preferences.mode === 'system') {
      return systemDark ? 'dark' : 'light';
    }
    return preferences.mode;
  }, [preferences.mode, systemDark]);

  // Load / unload dyslexic font
  useEffect(() => {
    ensureDyslexicFont(preferences.fontFamily === 'dyslexic');
  }, [preferences.fontFamily]);

  // Persist + update
  const setPreferences = useCallback((prefs: ThemePreferences) => {
    writePreferences(prefs);
    setPreferencesState(prefs);
  }, []);

  // Build the MUI theme, memoised on the values that matter
  const computedTheme = useMemo(
    () =>
      createCadenceTheme({
        mode: resolvedMode,
        highContrast: preferences.highContrast,
        fontFamily: preferences.fontFamily,
        fontSize: preferences.fontSize,
        reducedMotion: preferences.reducedMotion,
      }),
    [
      resolvedMode,
      preferences.highContrast,
      preferences.fontFamily,
      preferences.fontSize,
      preferences.reducedMotion,
    ],
  );

  const contextValue = useMemo<ThemePreferencesContextValue>(
    () => ({ preferences, setPreferences, resolvedMode }),
    [preferences, setPreferences, resolvedMode],
  );

  return (
    <ThemePreferencesContext.Provider value={contextValue}>
      <MuiThemeProvider theme={computedTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemePreferencesContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useThemePreferences(): ThemePreferencesContextValue {
  const ctx = useContext(ThemePreferencesContext);
  if (!ctx) {
    throw new Error(
      'useThemePreferences must be used within a CadenceThemeProvider',
    );
  }
  return ctx;
}
